import { Ajuda } from '../../design/Ajuda';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Sessao } from '../../auth';
import { ErroDeArea } from '../../design/Estados';
import {
  bloquearVenda,
  diaCurto,
  lerBloqueadosDoDia,
  lerCasosDaReposicao,
  lerListaDeBloqueados,
  liberarBloqueioDeVenda,
  salvarRegraDoBloqueio,
} from '../../giro';
import type {
  BloqueioDaLista,
  CasoDaReposicao,
  LinhaDoDiaDeBloqueio,
  PainelDeBloqueiosDoDia,
  RegraDoBloqueio,
} from '../../giro';
import '../paineis/visao-geral.css';
import { SubnavDoGiro } from './SubnavDoGiro';

/**
 * BLOQUEADOS (Carlos + dono, 08/10/2026) — onde o gestor vê, todo dia, quanto cada balconista
 * está errando na reposição do Giro e quem está bloqueado.
 *
 *   % de erro = o auditor achou o que ele disse que não tinha ÷ itens que ele foi mandado repor.
 *   ≥ limite (30%) → vendas bloqueadas no dia seguinte, o dia inteiro; libera sozinho.
 *
 * O servidor decide tudo (a conta, o bloqueio, o fim); a tela só mostra e pede as ações. Caso que
 * o auditor ainda não conferiu não pesa contra ninguém: aparece como "esperando" e mostra até
 * onde a taxa pode chegar. O produto só aparece aqui, para a gestão — nunca para o balconista.
 */

type Aba = 'dia' | 'agora' | 'historico';

const hojeBr = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
const somar = (dia: string, n: number) =>
  new Date(Date.parse(`${dia}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const pct = (n: number) => `${n.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
const quando = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

function falta(iso: string | null, agora: number): string {
  if (!iso) return 'até o gestor liberar';
  const ms = Date.parse(iso) - agora;
  if (ms <= 0) return 'terminando';
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `faltam ${String(h)} h ${String(m)} min` : `faltam ${String(m)} min`;
}

const SITUACAO: Record<BloqueioDaLista['situacao'], { rotulo: string; classe: string }> = {
  BLOQUEADO: { rotulo: 'Bloqueado agora', classe: 'gr-st-div' },
  AGENDADO: { rotulo: 'Bloqueia no dia', classe: 'gr-st-rep' },
  LIBERADO_PELO_GESTOR: { rotulo: 'Liberado pelo gestor', classe: 'gr-st-ok' },
  TERMINOU: { rotulo: 'Terminou (liberou sozinho)', classe: '' },
};
const ORIGEM: Record<BloqueioDaLista['origem'], string> = {
  PERCENTUAL: 'Automático (%)',
  MANUAL: 'Manual',
  POR_ERRO: 'Antigo (por erro)',
};
const NIVEL: Record<LinhaDoDiaDeBloqueio['nivel'], { rotulo: string; classe: string }> = {
  BLOQUEIA: { rotulo: 'Passou do limite', classe: 'gr-st-div' },
  POUCOS_ITENS: { rotulo: 'Passou, poucos itens', classe: 'gr-st-rep' },
  PERTO: { rotulo: 'Perto do limite', classe: 'gr-st-rep' },
  OK: { rotulo: 'Dentro', classe: 'gr-st-ok' },
  SEM_REPOSICAO: { rotulo: 'Nada a repor', classe: '' },
};

/** A resposta de uma escrita em frase para a tela. */
function falhaDe(r: { tipo: string; mensagem?: string }): string | null {
  if (r.tipo === 'confirmada') return null;
  if (r.tipo === 'recusada') return r.mensagem ?? 'O servidor recusou.';
  return 'Sem resposta do servidor: recarregue antes de repetir.';
}

export function BloqueadosDoGiro({ sessao }: { sessao: Sessao }) {
  const [aba, setAba] = useState<Aba>('dia');
  const [dia, setDia] = useState(() => somar(hojeBr(), -1));
  const [painel, setPainel] = useState<PainelDeBloqueiosDoDia | null>(null);
  const [lista, setLista] = useState<readonly BloqueioDaLista[] | null>(null);
  const [erro, setErro] = useState(false);
  const [agora, setAgora] = useState(() => Date.now());

  const carregar = useCallback(() => {
    setErro(false);
    setPainel(null);
    void lerBloqueadosDoDia(dia).then((r) => (r.ok ? setPainel(r.dados) : setErro(true)));
    void lerListaDeBloqueados().then((r) => (r.ok ? setLista(r.dados) : setErro(true)));
  }, [dia]);
  useEffect(carregar, [carregar]);
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  const ativos =
    lista?.filter((b) => b.situacao === 'BLOQUEADO' || b.situacao === 'AGENDADO') ?? [];

  // Dono 21/10: abria no "Dia a dia" de ontem e parecia que ninguém estava bloqueado. Com alguém
  // bloqueado hoje ou amanhã, a tela abre em "Bloqueados agora" — só na 1ª carga, sem brigar com o clique.
  const abaEscolhida = useRef(false);
  useEffect(() => {
    if (!abaEscolhida.current && lista) {
      abaEscolhida.current = true;
      if (ativos.length > 0) setAba('agora');
    }
  }, [lista, ativos.length]);

  return (
    <section className="cv gr">
      <SubnavDoGiro atual="giro-bloqueados" />
      <div className="cv-corpo">
        <div className="cv-titulo cv-titulo-2l">
          <h1>Bloqueados</h1>
          <span>
            Erro na reposição do Giro, por balconista: o auditor achou o que ele disse que não tinha
            ÷ itens que ele foi mandado repor. Passou do limite, as vendas dele ficam bloqueadas no
            dia seguinte e liberam sozinhas.
          </span>
        </div>

        {erro ? (
          <ErroDeArea oQue="os bloqueios do Giro" aoTentar={carregar} />
        ) : (
          <>
            {painel && <Regra sessao={sessao} regra={painel.regra} aoSalvar={carregar} />}

            <div className="vg-segmentos cv-abas" role="tablist" aria-label="O que ver">
              {(
                [
                  ['dia', 'Dia a dia', null],
                  ['agora', 'Bloqueados agora', ativos.length],
                  ['historico', 'Histórico', null],
                ] as const
              ).map(([k, rotulo, n]) => (
                <button
                  key={k}
                  type="button"
                  role="tab"
                  aria-selected={aba === k}
                  className={`vg-seg${aba === k ? ' vg-seg-ativo' : ''}`}
                  onClick={() => {
                    abaEscolhida.current = true;
                    setAba(k);
                  }}
                >
                  {rotulo}
                  {n !== null && lista ? <em className="cv-seg-num">{n}</em> : null}
                </button>
              ))}
            </div>

            {aba === 'dia' && (
              <DiaADia
                sessao={sessao}
                dia={dia}
                setDia={setDia}
                painel={painel}
                aoMudar={carregar}
              />
            )}
            {aba === 'agora' && (
              <ListaDeBloqueios
                sessao={sessao}
                itens={lista ? ativos : null}
                agora={agora}
                vazio="Ninguém bloqueado agora nem agendado para amanhã."
                aoMudar={carregar}
              />
            )}
            {aba === 'historico' && (
              <ListaDeBloqueios
                sessao={sessao}
                itens={lista}
                agora={agora}
                vazio="Nenhum bloqueio nos últimos 30 dias."
                aoMudar={carregar}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
}

/* ------------------------------ a regra (% editável) ------------------------------ */

function Regra({
  sessao,
  regra,
  aoSalvar,
}: {
  sessao: Sessao;
  regra: RegraDoBloqueio;
  aoSalvar: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const [ativo, setAtivo] = useState(regra.ativo);
  const [limite, setLimite] = useState(String(regra.limitePct));
  const [minimo, setMinimo] = useState(String(regra.minimoMandados));
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const abrir = () => {
    setAtivo(regra.ativo);
    setLimite(String(regra.limitePct));
    setMinimo(String(regra.minimoMandados));
    setMotivo('');
    setFalha(null);
    setEditando(true);
  };
  return (
    <div className="cv-cartao">
      <div className="cv-cartao-topo">
        <div>
          <b>
            Regra
            <Ajuda rotulo="a regra do bloqueio">
              Erro = o auditor achou o produto que o balconista disse que não tinha. Se os erros
              passarem da porcentagem (dos itens que ele foi mandado repor), as vendas dele no
              InovaPay ficam bloqueadas no dia seguinte e liberam sozinhas depois. Quem foi mandado
              repor poucos itens não é bloqueado. É a mesma régua da tela Qualidade da Equipe: subir
              ou descer aqui muda lá também, e vale para as próximas confirmações (bloqueio já
              criado não muda).
            </Ajuda>
            :{' '}
          </b>
          <span className={`gr-st ${regra.ativo ? 'gr-st-ok' : ''}`}>
            {regra.ativo ? 'Ligada' : 'Desligada'}
          </span>{' '}
          bloqueia com <b>{pct(regra.limitePct)} ou mais</b> de erro, se ele foi mandado repor pelo
          menos <b>{regra.minimoMandados}</b> itens · conta Giro a partir de {diaCurto(regra.desde)}
          {regra.motivo && (
            <span className="cv-cinza cv-peq"> · última mudança: {regra.motivo}</span>
          )}
        </div>
        {!editando && (
          <button type="button" className="cv-botao" onClick={abrir}>
            Editar regra
          </button>
        )}
      </div>
      {editando && (
        <div className="gr-capac-acoes">
          <label>
            <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />{' '}
            Regra ligada
          </label>
          <label>
            <span className="gr-rotulo">LIMITE (%)</span>
            <input
              className="gr-input gr-input-curto"
              inputMode="numeric"
              value={limite}
              onChange={(e) => setLimite(e.target.value.replace(/\D/g, ''))}
            />
          </label>
          <label>
            <span className="gr-rotulo">MÍNIMO DE ITENS MANDADOS REPOR</span>
            <input
              className="gr-input gr-input-curto"
              inputMode="numeric"
              value={minimo}
              onChange={(e) => setMinimo(e.target.value.replace(/\D/g, ''))}
            />
          </label>
          <label className="gr-capac-motivo">
            <span className="gr-rotulo">POR QUE ESTÁ MUDANDO</span>
            <input
              className="gr-input"
              value={motivo}
              maxLength={300}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="cv-botao-primario"
            disabled={salvando || motivo.trim().length < 5 || !limite || !minimo}
            onClick={() => {
              setSalvando(true);
              setFalha(null);
              void salvarRegraDoBloqueio(sessao, {
                ativo,
                limitePct: Number(limite),
                minimoMandados: Number(minimo),
                motivo: motivo.trim(),
              }).then((r) => {
                setSalvando(false);
                const f = falhaDe(r);
                if (f) setFalha(f);
                else {
                  setEditando(false);
                  aoSalvar();
                }
              });
            }}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
          <button type="button" className="cv-botao" onClick={() => setEditando(false)}>
            Cancelar
          </button>
          <p className="cv-cinza cv-peq">
            Vale para as próximas auditorias. Quem já está bloqueado continua; dia passado não é
            refeito.
          </p>
          {falha && (
            <p className="cv-falha" role="alert">
              {falha}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------ dia a dia ------------------------------ */

function DiaADia({
  sessao,
  dia,
  setDia,
  painel,
  aoMudar,
}: {
  sessao: Sessao;
  dia: string;
  setDia: (d: string) => void;
  painel: PainelDeBloqueiosDoDia | null;
  aoMudar: () => void;
}) {
  const [aberto, setAberto] = useState<string | null>(null);
  const [bloqueando, setBloqueando] = useState<string | null>(null);
  const hoje = hojeBr();
  const linhas = painel?.linhas ?? [];
  const soma = (k: 'mandados' | 'erros' | 'esperando') => linhas.reduce((t, l) => t + l[k], 0);
  return (
    <>
      <div className="cv-cartao-topo">
        <div className="gr-capac-acoes">
          <button type="button" className="cv-botao" onClick={() => setDia(somar(dia, -1))}>
            ◀ Dia anterior
          </button>
          <label>
            <span className="gr-rotulo">GIRO DO DIA</span>
            <input
              type="date"
              className="gr-input"
              value={dia}
              max={hoje}
              onChange={(e) => e.target.value && setDia(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="cv-botao"
            disabled={dia >= hoje}
            onClick={() => setDia(somar(dia, 1))}
          >
            Próximo dia ▶
          </button>
          <button type="button" className="cv-botao" onClick={() => setDia(hoje)}>
            Hoje
          </button>
        </div>
      </div>

      {painel?.foraDaRegra && (
        <p className="cv-aviso">
          Giro de antes do início da regra ({diaCurto(painel.regra.desde)}): a conta aparece, mas
          não bloqueia ninguém.
        </p>
      )}

      <div className="cv-kpis cv-kpis-5">
        <Kpi
          rotulo="BALCONISTAS"
          ajuda="Quantos balconistas fizeram Giro neste dia."
          valor={painel ? linhas.length : null}
        />
        <Kpi
          rotulo="MANDADOS REPOR"
          ajuda="Quantos itens o sistema mandou repor, somando todos os balconistas do dia."
          valor={painel ? soma('mandados') : null}
        />
        <Kpi
          rotulo="AUDITOR ACHOU (ERROS)"
          ajuda="Itens em que o balconista disse que não tinha para repor, mas o auditor achou. Cada um conta como erro."
          valor={painel ? soma('erros') : null}
        />
        <Kpi
          rotulo="ESPERANDO O AUDITOR"
          ajuda="Itens que o auditor ainda não conferiu. Enquanto esperam, não contam contra ninguém."
          valor={painel ? soma('esperando') : null}
          sub="ainda não pesam contra ninguém"
        />
        <Kpi
          rotulo="BLOQUEADOS POR ESTE GIRO"
          ajuda="Balconistas que passaram do limite da regra neste Giro: as vendas deles ficam bloqueadas no dia seguinte."
          valor={painel ? linhas.filter((l) => l.bloqueio).length : null}
        />
      </div>

      <div className="cv-cartao cv-sem-pad">
        {!painel ? (
          <div className="cv-carregando" aria-busy="true" />
        ) : linhas.length === 0 ? (
          <p className="cv-vazio">Nenhum Giro neste dia.</p>
        ) : (
          <div className="cv-rolagem">
            <table className="cv-tabela">
              <thead>
                <tr>
                  <th>Balconista</th>
                  <th className="cv-dir">Contou</th>
                  <th className="cv-dir">Mandados repor</th>
                  <th className="cv-dir">Repôs</th>
                  <th className="cv-dir">Auditor achou</th>
                  <th className="cv-dir">Estava certo</th>
                  <th className="cv-dir">Esperando auditor</th>
                  <th className="cv-dir">% erro</th>
                  <th>Situação</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => (
                  <LinhaDoBalconista
                    key={l.accountId}
                    sessao={sessao}
                    l={l}
                    dia={dia}
                    hoje={hoje}
                    aberto={aberto === l.accountId}
                    alternar={() => setAberto(aberto === l.accountId ? null : l.accountId)}
                    bloqueando={bloqueando === l.accountId}
                    alternarBloqueio={() =>
                      setBloqueando(bloqueando === l.accountId ? null : l.accountId)
                    }
                    aoMudar={() => {
                      setBloqueando(null);
                      aoMudar();
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function Kpi({
  rotulo,
  valor,
  sub,
  ajuda,
}: {
  rotulo: string;
  valor: number | null;
  sub?: string;
  ajuda: string;
}) {
  return (
    <div className="cv-kpi">
      <div className="cv-kpi-rotulo">
        {rotulo}
        <Ajuda rotulo={rotulo.toLowerCase()}>{ajuda}</Ajuda>
      </div>
      <div className="cv-kpi-valor">{valor === null ? '—' : valor}</div>
      {sub && <div className="cv-kpi-sub">{sub}</div>}
    </div>
  );
}

function LinhaDoBalconista({
  sessao,
  l,
  dia,
  hoje,
  aberto,
  alternar,
  bloqueando,
  alternarBloqueio,
  aoMudar,
}: {
  sessao: Sessao;
  l: LinhaDoDiaDeBloqueio;
  dia: string;
  hoje: string;
  aberto: boolean;
  alternar: () => void;
  bloqueando: boolean;
  alternarBloqueio: () => void;
  aoMudar: () => void;
}) {
  const nivel = NIVEL[l.nivel];
  return (
    <>
      <tr>
        <td>
          <b>{l.nome}</b>
          <div className="cv-cinza cv-peq">{l.setores.join(', ')}</div>
        </td>
        <td className="cv-dir">
          {l.contados}/{l.itens}
        </td>
        <td className="cv-dir">{l.mandados}</td>
        <td className="cv-dir">{l.repostos}</td>
        <td className="cv-dir">
          <b>{l.erros}</b>
        </td>
        <td className="cv-dir">{l.corretos}</td>
        <td className="cv-dir">{l.esperando}</td>
        <td className="cv-dir">
          <b>{l.mandados ? pct(l.taxa) : '—'}</b>
          {l.esperando > 0 && l.taxaMaxima > l.taxa && (
            <div className="cv-cinza cv-peq">pode chegar a {pct(l.taxaMaxima)}</div>
          )}
        </td>
        <td>
          {l.bloqueio ? (
            <span className={`gr-st ${SITUACAO[l.bloqueio.situacao].classe}`}>
              {SITUACAO[l.bloqueio.situacao].rotulo} {diaCurto(l.bloqueio.dia)}
            </span>
          ) : (
            <span className={`gr-st ${nivel.classe}`}>{nivel.rotulo}</span>
          )}
        </td>
        <td>
          <button type="button" className="gr-link-botao" onClick={alternar} aria-expanded={aberto}>
            {aberto ? 'Fechar' : 'Ver casos'}
          </button>{' '}
          <button type="button" className="gr-link-botao" onClick={alternarBloqueio}>
            Bloquear
          </button>
        </td>
      </tr>
      {bloqueando && (
        <tr>
          <td colSpan={10}>
            <BloqueioManual sessao={sessao} accountId={l.accountId} hoje={hoje} aoMudar={aoMudar} />
          </td>
        </tr>
      )}
      {aberto && (
        <tr>
          <td colSpan={10}>
            <Casos accountId={l.accountId} dia={dia} />
          </td>
        </tr>
      )}
    </>
  );
}

const RESULTADO: Record<string, string> = {
  ERRO_BALCONISTA_CONFIRMADO: 'Auditor achou (erro)',
  BALCONISTA_CORRETO: 'Não tinha mesmo (certo)',
  INCONCLUSIVO: 'Inconclusivo',
  PROBLEMA_DADOS: 'Problema de dados',
  PRODUTO_MUDOU_LOCAL: 'Mudou de lugar',
};

function Casos({ accountId, dia }: { accountId: string; dia: string }) {
  const [casos, setCasos] = useState<readonly CasoDaReposicao[] | null>(null);
  const [erro, setErro] = useState(false);
  const carregar = useCallback(() => {
    setErro(false);
    void lerCasosDaReposicao(accountId, dia).then((r) =>
      r.ok ? setCasos(r.dados) : setErro(true),
    );
  }, [accountId, dia]);
  useEffect(carregar, [carregar]);
  if (erro) return <ErroDeArea oQue="os casos" aoTentar={carregar} />;
  if (!casos) return <div className="cv-carregando" aria-busy="true" />;
  if (casos.length === 0)
    return <p className="cv-vazio">Nenhuma divergência de reposição neste Giro.</p>;
  return (
    <table className="cv-tabela">
      <thead>
        <tr>
          <th>Caso</th>
          <th>Produto</th>
          <th className="cv-dir">Mandado repor</th>
          <th className="cv-dir">Ele achou</th>
          <th className="cv-dir">Auditor achou</th>
          <th>Resultado</th>
          <th>Auditor</th>
        </tr>
      </thead>
      <tbody>
        {casos.map((c) => (
          <tr key={c.divergenciaId}>
            <td>
              <a href={`#/caso?id=${c.divergenciaId}`}>
                {c.numero !== null ? `#${String(c.numero).padStart(4, '0')}` : 'abrir'}
              </a>
            </td>
            <td>
              {c.produto}
              {c.codigo && <span className="cv-cinza cv-peq"> · {c.codigo}</span>}
            </td>
            <td className="cv-dir">{c.pedido}</td>
            <td className="cv-dir">{c.encontrado}</td>
            <td className="cv-dir">{c.recontagem ?? '—'}</td>
            <td>
              {c.resultado ? (
                <span
                  className={`gr-st ${c.resultado === 'ERRO_BALCONISTA_CONFIRMADO' ? 'gr-st-div' : c.resultado === 'BALCONISTA_CORRETO' ? 'gr-st-ok' : ''}`}
                >
                  {RESULTADO[c.resultado] ?? c.resultado}
                </span>
              ) : (
                <span className="gr-st gr-st-rep">Esperando o auditor</span>
              )}
            </td>
            <td className="cv-peq">
              {c.auditor ?? '—'}
              {c.auditadoEm && <span className="cv-cinza"> · {quando(c.auditadoEm)}</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BloqueioManual({
  sessao,
  accountId,
  hoje,
  aoMudar,
}: {
  sessao: Sessao;
  accountId: string;
  hoje: string;
  aoMudar: () => void;
}) {
  const [dia, setDia] = useState(somar(hoje, 1));
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  return (
    <div className="gr-capac-acoes">
      <label>
        <span className="gr-rotulo">BLOQUEAR AS VENDAS EM</span>
        <select className="gr-input" value={dia} onChange={(e) => setDia(e.target.value)}>
          <option value={hoje}>Hoje ({diaCurto(hoje)})</option>
          <option value={somar(hoje, 1)}>Amanhã ({diaCurto(somar(hoje, 1))})</option>
        </select>
      </label>
      <label className="gr-capac-motivo">
        <span className="gr-rotulo">MOTIVO (O BALCONISTA LÊ)</span>
        <input
          className="gr-input"
          value={motivo}
          maxLength={300}
          onChange={(e) => setMotivo(e.target.value)}
        />
      </label>
      <button
        type="button"
        className="cv-botao-primario"
        disabled={salvando || motivo.trim().length < 5}
        onClick={() => {
          setSalvando(true);
          setFalha(null);
          void bloquearVenda(sessao, { conta: accountId, dia, motivo: motivo.trim() }).then((r) => {
            setSalvando(false);
            const f = falhaDe(r);
            if (f) setFalha(f);
            else aoMudar();
          });
        }}
      >
        {salvando ? 'Bloqueando…' : 'Bloquear 1 dia'}
      </button>
      {falha && (
        <p className="cv-falha" role="alert">
          {falha}
        </p>
      )}
    </div>
  );
}

/* ------------------------------ bloqueados agora / histórico ------------------------------ */

function ListaDeBloqueios({
  sessao,
  itens,
  agora,
  vazio,
  aoMudar,
}: {
  sessao: Sessao;
  itens: readonly BloqueioDaLista[] | null;
  agora: number;
  vazio: string;
  aoMudar: () => void;
}) {
  const [motivo, setMotivo] = useState<Record<string, string>>({});
  const [liberando, setLiberando] = useState<string | null>(null);
  const [falha, setFalha] = useState<string | null>(null);
  if (!itens) return <div className="cv-carregando" aria-busy="true" />;
  if (itens.length === 0) return <p className="cv-vazio">{vazio}</p>;
  return (
    <div className="cv-cartao cv-sem-pad">
      <div className="cv-rolagem">
        <table className="cv-tabela">
          <thead>
            <tr>
              <th>Balconista</th>
              <th>Tipo</th>
              <th>Por quê</th>
              <th>Período</th>
              <th>Situação</th>
              <th>Liberar antes</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((b) => {
              const s = SITUACAO[b.situacao];
              const vale = b.situacao === 'BLOQUEADO' || b.situacao === 'AGENDADO';
              return (
                <tr key={b.id}>
                  <td>
                    <b>{b.nome}</b>
                  </td>
                  <td className="cv-peq">{ORIGEM[b.origem]}</td>
                  <td className="cv-peq">
                    {b.origem === 'PERCENTUAL' ? (
                      <>
                        <b>{pct(b.taxa ?? 0)}</b> de erro ({b.erros} de {b.mandados}) no Giro de{' '}
                        {diaCurto(b.diaGiro ?? b.dia)} · limite {pct(b.limite ?? 0)}
                      </>
                    ) : b.origem === 'MANUAL' ? (
                      <>
                        {b.motivo}
                        {b.criadoPor && <span className="cv-cinza"> · por {b.criadoPor}</span>}
                      </>
                    ) : (
                      b.porque
                    )}
                  </td>
                  <td className="cv-peq">
                    {b.ate && b.ate !== b.dia
                      ? `${diaCurto(b.dia)} a ${diaCurto(b.ate)}`
                      : diaCurto(b.dia)}
                    {b.situacao === 'BLOQUEADO' && (
                      <div className="cv-cinza">{falta(b.terminaEm, agora)}</div>
                    )}
                    {b.situacao === 'AGENDADO' && b.terminaEm && (
                      <div className="cv-cinza">libera {quando(b.terminaEm)}</div>
                    )}
                  </td>
                  <td>
                    <span className={`gr-st ${s.classe}`}>{s.rotulo}</span>
                    {b.liberado && (
                      <div className="cv-cinza cv-peq">
                        {b.liberado.por} em {quando(b.liberado.em)}: {b.liberado.motivo}
                      </div>
                    )}
                  </td>
                  <td>
                    {vale && (
                      <div className="gr-capac-acoes">
                        <input
                          className="gr-input"
                          placeholder="Motivo para liberar"
                          aria-label={`Motivo para liberar ${b.nome}`}
                          value={motivo[b.id] ?? ''}
                          maxLength={300}
                          onChange={(e) => setMotivo({ ...motivo, [b.id]: e.target.value })}
                        />
                        <button
                          type="button"
                          className="cv-botao-primario"
                          disabled={liberando !== null || (motivo[b.id] ?? '').trim().length < 5}
                          onClick={() => {
                            setFalha(null);
                            setLiberando(b.id);
                            void liberarBloqueioDeVenda(
                              sessao,
                              b.id,
                              (motivo[b.id] ?? '').trim(),
                            ).then((r) => {
                              setLiberando(null);
                              const f = falhaDe(r);
                              if (f) setFalha(f);
                              else aoMudar();
                            });
                          }}
                        >
                          {liberando === b.id ? 'Liberando…' : 'Liberar'}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {falha && (
        <p className="cv-falha cv-pad" role="alert">
          {falha}
        </p>
      )}
    </div>
  );
}
